<template>
    <div ref="weSingleSelect">
        <div v-if="!selectedOption" class="weact-form-select" :class="classes.wrapper">
            <input
                ref="search"
                :disabled="disabled"
                :class="[classes.input, isRequired]"
                :placeholder="placeholder"
                :required="required"
                :readonly="disabled"
                @focus="seedSearchText()"
                @keydown.enter.prevent="setOption()"
                @keyup.enter.prevent="setOption()"
                @keyup.down="movePointerDown()"
                @keyup.tab.stop="closeOut()"
                @keyup.esc.stop="closeOut()"
                @keyup.up="movePointerUp()"
                autocomplete="off"
                v-model="searchText"
            />

            <div
                @click="toggleDropdown"
                :class="[classes.icons]"
                class="icon"
            >
                <i class="pe-7s-angle-down"></i>
            </div>

            <ul
                tabindex="-1"
                ref="options"
                v-if="matchingOptions && matchingOptions.length > 0"
                :style="{'max-height': maxHeight}"
                :class="[classes.dropdown]"
            >
                <li
                    tabindex="-1"
                    v-for="(option, index) in matchingOptions"
                    :key="index"
                    :class="index === pointer ? 'active' : ''"
                    @mouseover="setPointerIndex(index)"
                    @keyup.enter="setOption()"
                    @keyup.up="movePointerUp()"
                    @keyup.down="movePointerDown()"
                    @click.prevent="setOption()"
                >
                    <slot name="option" v-bind="{option,index}">{{ getOptionDescription(option) }}</slot>
                </li>
            </ul>
        </div>

        <div :class="classes.wrapper" class="weact-form-select" v-if="selectedOption">
            <input
                ref="match"
                :class="[classes.input]"
                :required="required"
                :readonly="disabled"
                :value="getOptionDescription(selectedOption)"
                @focus="switchToSearch($event)"
            />
            <input type="hidden" :name="name" ref="selectedValue" :value="getOptionValue(selectedOption)" />

            <div
                :class="[classes.icons]"
                class="icon"
            >
                <i class="pe-7s-close" @click="closeOut()"></i>
            </div>
        </div>
    </div>
</template>

<script>
import pointerScroll from '../lib/pointerScroll';

export default {
    props: {
        value: {
            required: true
        },
        name: {
            type: String,
            required: false,
            default: () => ''
        },
        options: {
            type: Array,
            required: false,
            default: () => []
        },
        optionLabel: {
            type: String,
            required: false,
            default: () => null
        },
        optionKey: {
            type: String,
            required: false,
            default: () => null
        },
        placeholder: {
            type: String,
            required: false,
            default: () => ''
        },
        maxHeight: {
            type: String,
            default: () => '250px',
            required: false
        },
        classes: {
            type: Object,
            required: false,
            default: () => null
        },
        initial: {
            type: String,
            required: false,
            default: () => null
        },
        initKeySelected: {
            type: [String, Number],
            required: false,
            default: () => null
        },
        disabled: {
            type: Boolean,
            required: false,
            default: () => false
        },
        required: {
            type: Boolean,
            required: false,
            default: () => false
        },
        tabindex: {
            type: String,
            required: false,
            default: () => {
                return '';
            }
        },
        getOptionDescription: {
            type: Function,
            default: function(option) {
                if (this.optionLabel) {
                    return option[this.optionLabel];
                }
                
                return option;
            }
        },
        getOptionValue: {
            type: Function,
            default: function(option) {
                if (this.optionKey) {
                    return option[this.optionKey];
                }

                if (this.optionLabel) {
                    return option[this.optionLabel];
                }

                return option;
            }
        },
        filterBy: {
            type: Function,
            default: function(option) {
                if (this.optionLabel) {
                    return option[this.optionLabel]
                        .toString()
                        .toLowerCase()
                        .includes(this.searchText.toString().toLowerCase());
                }

                return option
                        .toString()
                        .toLowerCase()
                        .includes(this.searchText.toString().toLowerCase());
            }
        }
    },
    mixins: [pointerScroll],
    mounted() {
        document.addEventListener('click', this.handleClickOutside);
        document.addEventListener('keyup', this.handleClickOutside);
        
        if (this.value && this.options.includes(this.value)) {
            this.selectedOption = this.value;
            return;
        }

        this.searchText = this.initial;
        this.setInitSelectedOption();
    },
    destroyed() {
        document.removeEventListener('keyup', this.handleClickOutside);
        document.removeEventListener('click', this.handleClickOutside);
    },
    data() {
        return {
            searchReady: false,
            searchText: null,
            selectedOption: null,
            dropdownOpen: false
        };
    },
    watch: {
        value(curr) {
            this.selectedOption = curr;
        },
        options() {
            this.setInitSelectedOption();
        },
        searchText(curr, prev) {
            if (curr !== prev) {
                this.pointer = -1;
            }
        },
        selectedOption(curr) {
            this.$emit('input', curr);
        },
        dropdownOpen(curr, prev) {
            if (curr === prev) {
                return;
            }
            if (!curr) {
                this.searchText = null;
                return;
            }
            if (!this.searchText) {
                this.searchText = '';
            }
        }
    },
    computed: {
        isRequired() {
            if (!this.required) {
                return '';
            }

            if (this.selectedOption) {
                return '';
            }

            return 'required';
        },
        matchingOptions() {
            if (!this.searchReady || this.searchText === null) {
                return null;
            }

            if (!this.searchText.length) {
                return [...this.options];
            }

            return this.options
                .filter(option => this.filterBy(option));
        }
    },
    methods: {
        setPointerIndex(index) {
            this.pointer = index;
        },
        seedSearchText() {
            if (this.disabled) {
                return;
            }

            this.searchReady = true;

            if (this.searchText !== null) {
                return;
            }

            this.searchText = '';
        },
        switchToSearch(event) {
            if (this.disabled) {
                return;
            }

            this.$refs.selectedValue.value = null;
            this.searchText = event.target.value;
            this.selectedOption = null;
            this.dropdownOpen = true;
        },
        toggleDropdown() {
            if (this.disabled) {
                return;
            }

            this.dropdownOpen = !this.dropdownOpen;
        },
        closeOut() {
            if (this.disabled) {
                return;
            }

            this.selectedOption = null;
            this.dropdownOpen = false;
            this.searchText = null;
        },
        movePointerDown() {
            if (!this.matchingOptions) {
                return;
            }
            if (this.pointer >= this.matchingOptions.length - 1) {
                return;
            }

            this.pointer++;
        },
        movePointerUp() {
            if (this.pointer > 0) {
                this.pointer--;
            }
        },
        setInitSelectedOption() {
            let page = this;
            let item = -1;

            setTimeout(function () {
                for (var index = 0; index < page.options.length; index++) {
                    if (page.options[index][page.optionKey] == page.initKeySelected) {
                        item = index;
                    }
                }

                if (item > -1) {
                    page.searchReady = true;
                    page.pointer = item;
                    page.searchText = '';
                    page.setOption();
                }
            }, 100);
        },
        setOption() {
            if (!this.matchingOptions || !this.matchingOptions.length) {
                return;
            }

            if (this.pointer === -1) {
                this.pointer++;
            }

            this.selectedOption = this.matchingOptions[this.pointer];
            this.searchText = null;
            this.dropdownOpen = false;
            this.pointer = -1;
        },
        handleClickOutside(e) {
            if (this.$el.contains(e.target)) {
                return;
            }

            this.dropdownOpen = false;
            this.searchText = null;
        }
    }
};
</script>

<style lang="scss">
@import '@/weact/scss/Variables.scss';

.weact-form-select {
    position: relative;

    .icon {
        display: table;
        position: absolute;
        z-index: 0;
        right: 0;
        top: 0;
        bottom: 0;
        height: 100%;
        cursor: pointer;

        i {
            display: table-cell;
            height: 100%;
            vertical-align: middle;
            font-size: 32px;
            color: #000;
        }
    }

    ul {
        position: absolute;
        border: 1px solid #ccc;
        background: #fff;
        list-style: none;
        margin: 0;
        padding: 0;
        overflow: auto;
        z-index: 1001;
        width: 100%;
        max-height: 250px;

        li {
            cursor: pointer;
            font-size: 15px;
            padding: 10px;
            @include transition(none);

            &:hover, &.active {
                color: #fff;
                background: #3a9ed0;
            }
        }
    }
}
</style>